import React, {ChangeEvent, FormEvent, useEffect, useState} from 'react'
import {getLanguages, getGenders, getLanguageCodes, textToSpeech, Gender} from './TTS'
import {Toaster, toast} from 'react-hot-toast'
import Outage from './Outage'
import {ToggleButton} from 'react-bootstrap'
import DropdownToggle from 'react-bootstrap/esm/DropdownToggle'
import {useAuth} from '../../auth'

function TextToSpeech() {
  const [generating, setGenerating] = useState<boolean>(false)
  const [generated, setGenerated] = useState<boolean>(false)
  const [text, setText] = useState<string>('')
  const [languageCodes, setLanguageCodes] = useState<string[]>([])
  const [languageNames, setLanguageNames] = useState<string[]>([])

  const [languageCode, setLanguageCode] = useState<string>('')
  const [languageName, setLanguageName] = useState<string>('')
  const [gender, setGender] = useState<Gender>('NEUTRAL')
  const [pitch, setPitch] = useState<number>(0)
  const [speakingRate, setSpeakingRate] = useState<number>(1)
  const [audioBlob, setAudioBlob] = useState<Blob | null>()

  const {auth} = useAuth()

  const fetchLanguageCodes = () => {
    getLanguageCodes(auth?.token).then((res) => {
      setLanguageCodes(res)
      if (res.length) {
        setLanguageCode('en-US')
        getLanguages('en-US', auth?.token).then((resN) => {
          if (resN.length) {
            setLanguageNames(resN)
            setLanguageName(resN[0])
            getGenders('en-US', resN[0], auth?.token).then((resG) => {
              if (resG.length) {
                setGender(resG[0])
              }
            })
          }
        })
      }
    })
  }

  const fetchLanguageName = (languageCode: string) => {
    getLanguages(languageCode, auth?.token).then((resN) => {
      if (resN.length) {
        setLanguageNames(resN)
        setLanguageName(resN[0])
        getGenders(languageCode, resN[0]).then((resG) => {
          if (resG.length) {
            setGender(resG[0])
          }
        })
      }
    })
  }

  const fetchGenders = (languageName: string) => {
    getGenders(languageCode, languageName, auth?.token).then((resG) => {
      if (resG.length) {
        setGender(resG[0])
      }
    })
  }

  useEffect(() => {
    fetchLanguageCodes()
  }, [])

  const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setAudioBlob(null)
    setText(e.target.value)
  }

  const handleLanguageCodeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setLanguageCode(e.target.value)
    setAudioBlob(null)
    fetchLanguageName(e.target.value)
  }

  const handleLanguageNameChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setLanguageName(e.target.value)
    setAudioBlob(null)
    fetchGenders(e.target.value)
  }

  const handlePitchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAudioBlob(null)
    setPitch(parseInt(e.target.value))
  }

  const handleSpeedChange = (e: ChangeEvent<HTMLInputElement>) => {
    setAudioBlob(null)
    console.log(e.target.value)
    setSpeakingRate(parseFloat(e.target.value))
  }

  const handleDownload = () => {
    if (audioBlob) {
      const blobUrl = URL.createObjectURL(audioBlob)
      const a = document.createElement('a')
      a.href = blobUrl
      a.download = 'audio.wav'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)
    }
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!text) {
      return toast.error('Please enter text')
    }
    if (languageCode && languageName && gender && (pitch || pitch === 0)) {
      setGenerating(true)
      try {
        const audio = await textToSpeech(
          auth?.token,
          text,
          languageCode,
          languageName,
          gender,
          pitch,
          speakingRate
        )

        console.log(audio)
        setAudioBlob(audio)

        setGenerating(false)
        setGenerated(true)
      } catch (error: any) {
        setGenerated(false)
        setGenerating(false)
        console.error('Error:', error?.response ? error.response.data : error.message)
        return null
      }
    } else {
      toast.error('Cannot generate, some fields are empty')
    }
  }
  return (
    <div className='container'>
      <Toaster />
      <Outage />

      <form onSubmit={handleSubmit} noValidate className='form'>
        <div className='form-label'>
          <h2>Text to speech </h2>
        </div>
        <div className='card-body border-top p-9'>
          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label required fw-bold fs-6'>Text</label>

            <div className='col-lg-8'>
              <div className='row'>
                <textarea
                  value={text}
                  onChange={handleTextChange}
                  rows={5}
                  style={{width: '100%'}}
                  className='form-control form-control-xlg form-control-solid mb-3 mb-lg-0 w-100'
                  placeholder='Enter text'
                />
              </div>
            </div>
          </div>

          <div className='settings'>
            <div className='d-flex flex-column flex-md-row  justify-content-between'>
              <div className='row col-lg-6 mb-6'>
                <label className='col-lg-4 col-form-label fw-bold fs-6'>
                  <span className='required'>Language</span>
                </label>

                <div className='col-lg-6 fv-row'>
                  <select
                    value={languageCode || ''}
                    onChange={handleLanguageCodeChange}
                    className='form-select form-select-solid bg-secondary form-select-lg fw-bold'
                  >
                    {languageCodes ? (
                      languageCodes.map((lc) => <option value={lc}>{lc}</option>)
                    ) : (
                      <option value={''}>Select language</option>
                    )}
                  </select>
                </div>
              </div>
              <div className='row col-lg-5 mb-8'>
                <label className='col-lg-3 col-form-label fw-bold fs-6'>
                  <span className='required'>Voice</span>
                </label>

                <div className='col-lg-8 fv-row'>
                  <select
                    value={languageName || ''}
                    onChange={handleLanguageNameChange}
                    className='form-select form-select-solid bg-secondary form-select-lg fw-bold'
                  >
                    {languageNames ? (
                      languageNames.map((ln) => <option value={ln}>{ln}</option>)
                    ) : (
                      <option value=''>Select Voice</option>
                    )}
                  </select>
                </div>
              </div>
            </div>

            {/* <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>
              <span className='required'>Gender</span>
            </label>

            <div className='col-lg-8 fv-row'>
              <select
                value={gender || ''}
                onChange={handleVoiceGenderChange}
                className='form-select form-select-solid form-select-lg fw-bold'
              >
                {genders ? (
                  genders.map((gender) => <option value={gender}>{gender}</option>)
                ) : (
                  <option value={''}>Select gender...</option>
                )}
              </select>
            </div>
          </div> */}
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span className='required'>Pitch</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <label htmlFor='pitch'>{pitch}</label>
                <input
                  type='range'
                  id='pitch'
                  min={-20}
                  max={20}
                  step={0.5}
                  value={pitch}
                  onChange={handlePitchChange}
                  className='form-range form-range-solid form-range-lg fw-bold'
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label fw-bold fs-6'>
                <span className='required'>Speed</span>
              </label>

              <div className='col-lg-8 fv-row'>
                <label htmlFor='speed'>{speakingRate}</label>
                <input
                  type='range'
                  id='speed'
                  min={0.25}
                  max={4}
                  step={0.25}
                  value={speakingRate}
                  onChange={handleSpeedChange}
                  className='form-range form-range-solid form-range-lg fw-bold'
                />
              </div>
            </div>
          </div>
        </div>
        {audioBlob && (
          <div className='controllers d-flex w-100 flex-row justify-content-between align-items-center'>
            <audio onError={(e) => console.error('Audio error:', e)} controls>
              <source src={URL.createObjectURL(audioBlob)} type='audio/wav' />
            </audio>
          </div>
        )}

        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button type='submit' className='btn btn-primary' disabled={generating}>
            {!generating && 'Generate'}
            {generating && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}

export default TextToSpeech
